/* eslint-disable import/prefer-default-export */
import { JsonRpcProvider } from '@ethersproject/providers';
import { FeeAprData } from '../types';
// eslint-disable-next-line import/no-cycle
import { validateVaultData } from './vault';
import { graphUrls } from '../graphql/constants';
import getGraphUrls from '../utils/getGraphUrls';
import cache from '../utils/cache';
import { FeeAprQueryResponse } from '../types/vaultQueryData';
import { sendFeeAprQueryRequest } from '../graphql/functions';

/**
 * Get fee APR values for a vault from a V2 subgraph
 * @param vaultAddress The address of the vault
 * @param jsonProvider A JsonRpcProvider
 * @param dex The DEX identifier
 * @returns Fee APR data for different time periods or null if data not available
 */
export async function getFeeAprs(vaultAddress: string, jsonProvider: JsonRpcProvider): Promise<FeeAprData | null> {
  const key = `feeAprs-${vaultAddress}`;
  const cachedData = cache.get(key);
  if (cachedData) {
    return cachedData as FeeAprData;
  }

  // Cache for 30 minutes
  const ttl = 30 * 60 * 1000;

  const { chainId } = await validateVaultData(vaultAddress, jsonProvider);

  // Check if the subgraph is version 2
  const dexConfig = graphUrls[chainId];
  if (!dexConfig) {
    console.error(`This function is not supported on chain ${chainId}:`);
    return null;
  }

  const { publishedUrl, url } = getGraphUrls(chainId, true);

  try {
    let result: FeeAprQueryResponse | null = null;

    if (publishedUrl) {
      try {
        result = await sendFeeAprQueryRequest(publishedUrl, vaultAddress);
      } catch (error) {
        console.error('Request to published graph URL failed:', error);
      }
    }

    if (!result) {
      try {
        result = await sendFeeAprQueryRequest(url, vaultAddress);
      } catch (error) {
        console.error('Request to public graph URL failed:', error);
        return null;
      }
    }

    if (!result?.almVault) {
      return null;
    }

    const feeAprData: FeeAprData = {
      feeApr_1d: result.almVault.feeApr_1d ? result.almVault.feeApr_1d : null,
      feeApr_3d: result.almVault.feeApr_3d ? result.almVault.feeApr_3d : null,
      feeApr_7d: result.almVault.feeApr_7d ? result.almVault.feeApr_7d : null,
      feeApr_30d: result.almVault.feeApr_30d ? result.almVault.feeApr_30d : null,
    };

    cache.set(key, feeAprData, ttl);
    return feeAprData;
  } catch (error) {
    console.error(`Could not get fee APRs for vault ${vaultAddress} on chain ${chainId}:`, error);
    return null;
  }
}
